import { useId, useRef, useState, type ReactNode } from 'react';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Button } from './Button.js';
import { Icon } from './Icon.js';
import { cx } from './utils.js';

export type FileFieldProps = {
  accept?: string;
  disabled?: boolean;
  label?: string;
  emptyLabel?: string;
  className?: string;
  'aria-label'?: string;
  onChange: (file: File | null) => void;
};

/** Styled file picker — hides the native Browse chrome. */
export function FileField({
  accept,
  disabled = false,
  label,
  emptyLabel,
  className,
  'aria-label': ariaLabel,
  onChange,
}: FileFieldProps): ReactNode {
  const { text } = useAdminLocale();
  const resolvedLabel = label ?? text('Επιλογή αρχείου', 'Choose file');
  const resolvedEmptyLabel = emptyLabel ?? text('Δεν έχει επιλεγεί αρχείο', 'No file selected');
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  return (
    <div className={cx('flex flex-wrap items-center gap-2', className)} data-ui="file-field">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        aria-label={ariaLabel ?? resolvedLabel}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          setFileName(file?.name ?? null);
          onChange(file);
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="secondary"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Icon name="file" className="size-4" />
        {resolvedLabel}
      </Button>
      <span className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
        {fileName ?? resolvedEmptyLabel}
      </span>
    </div>
  );
}
